import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { API_ROUTES, INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { Fragment, useState } from "react";
import JourneyActionItemsDrawer from "features/NewHireJourney/Components/JourneyActionItemsDrawer";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { Link } from "react-router";
import { TbChevronRight } from "react-icons/tb";
import apiClient from "services/ApiClient";
import DashboardCard from "./DashboardCard";

interface TimelineIssue {
  id: number;
  title: string;
  description: string | null;
}

interface AssigneeTaskSummary {
  assigneeId: number | null;
  assigneeName: string;
  overdueCount: number;
  openCount: number;
  lastReminderAt: string | null;
}

type AttentionStatus = "needs_attention" | "tasks_overdue" | "tasks_open" | "all_clear";

interface TimelineJourney {
  id: string | number;
  stageKey: string | null;
  newHireName: string | null;
  title: string | null;
  startDate: string | null;
  attentionStatus: AttentionStatus;
  openTaskCount: number;
  overdueTaskCount: number;
  issues: TimelineIssue[];
  assigneeTaskSummaries: AssigneeTaskSummary[];
}

interface TimelineStage {
  key: string;
  label: string;
  subtitle: string;
  count: number;
  needsAttention: number;
  tasksOverdue: number;
  tasksOpen: number;
  allClear: number;
  journeys: TimelineJourney[];
}

interface TimelineData {
  totalCount: number;
  stages: TimelineStage[];
}

const STAGE_COLORS = {
  empty: { bg: "#F3F4F6", text: "#9CA3AF", border: "#E5E7EB" },
  pending: { bg: "#EDE9FE", text: "#7C3AED", border: "#DDD6FE" },
  needsAttention: { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" },
  open: { bg: "#EFF6FF", text: "#3B82F6", border: "#BFDBFE" },
  allClear: { bg: "#E6F7EF", text: "#2D8A57", border: "#BBF7D0" },
} as const;

function circleColors(stage: TimelineStage): { bg: string; text: string; border: string } {
  if (stage.count === 0) return STAGE_COLORS.empty;
  if (stage.key === "pending") return STAGE_COLORS.pending;
  if (stage.needsAttention + stage.tasksOverdue > 0) return STAGE_COLORS.needsAttention;
  if (stage.tasksOpen > 0) return STAGE_COLORS.open;
  return STAGE_COLORS.allClear;
}

const STATUS_DOT_COLOR: Record<AttentionStatus, string> = {
  needs_attention: STAGE_COLORS.needsAttention.text,
  tasks_overdue: STAGE_COLORS.needsAttention.text,
  tasks_open: STAGE_COLORS.open.text,
  all_clear: STAGE_COLORS.allClear.text,
};

function TimelineLegend() {
  const items = [
    { color: STAGE_COLORS.pending.text, label: "Pending kickoff" },
    { color: STAGE_COLORS.needsAttention.text, label: "Needs attention" },
    { color: STAGE_COLORS.open.text, label: "Open tasks" },
    { color: STAGE_COLORS.allClear.text, label: "All clear" },
  ];
  return (
    <Flex gap="14px" mt="10px" pt="8px" borderTop="0.5px solid" borderColor="gray.100" flexWrap="wrap" justify="center">
      {items.map((item) => (
        <HStack key={item.label} spacing="5px">
          <Box w="6px" h="6px" borderRadius="full" bg={item.color} flexShrink={0} />
          <Text fontSize="10px" color="content.tertiary">
            {item.label}
          </Text>
        </HStack>
      ))}
    </Flex>
  );
}

function StageNode({ stage, onClick }: { stage: TimelineStage; onClick: () => void }) {
  const colors = circleColors(stage);

  return (
    <Box
      as="button"
      onClick={onClick}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="4px"
      cursor="pointer"
      _hover={{ opacity: 0.8 }}
      transition="opacity 0.15s"
      minW="60px"
    >
      <Box
        w="36px"
        h="36px"
        borderRadius="full"
        bg={colors.bg}
        border="2px solid"
        borderColor={colors.border}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="12px" fontWeight="700" color={colors.text}>
          {stage.count}
        </Text>
      </Box>
      <Text fontSize="10px" fontWeight="600" color="content.primary" textAlign="center">
        {stage.label}
      </Text>
      {stage.subtitle && (stage.key === "mid_year" || stage.key === "late_year") && (
        <Text fontSize="9px" color="content.tertiary" textAlign="center" mt="-2px">
          {stage.subtitle}
        </Text>
      )}
    </Box>
  );
}

function PendingJourneyCard({ journey }: { journey: TimelineJourney }) {
  const pendingPath = INTERNAL_TEAM_ROUTES.newHireJourneys.pending.show
    .replace(":id", String(journey.id))
    .replace(":step", "1");

  return (
    <Box border="1px solid" borderColor="gray.100" borderRadius="10px" mb="8px" bg="white">
      <Flex align="center" gap="10px" px="12px" py="10px">
        <Avatar name={journey.newHireName ?? ""} size="sm" flexShrink={0} />
        <Box flex="1" minW={0}>
          <Text fontSize="13px" fontWeight="600" color="content.primary" noOfLines={1}>
            {journey.newHireName ?? "—"}
          </Text>
          {journey.title && (
            <Text fontSize="11px" color="content.tertiary" noOfLines={1}>
              {journey.title}
            </Text>
          )}
        </Box>
        <Box
          as={Link}
          to={pendingPath}
          px="10px"
          py="5px"
          borderRadius="6px"
          bg="blue.50"
          color="blue.600"
          fontSize="11px"
          fontWeight="600"
          flexShrink={0}
          whiteSpace="nowrap"
          _hover={{ bg: "blue.100" }}
          transition="background 0.1s"
        >
          Kick Off →
        </Box>
      </Flex>
    </Box>
  );
}

function formatTenureLabel(daysIn: number): string {
  if (daysIn < 14) return `Day ${daysIn + 1}`;
  if (daysIn < 60) return `Week ${Math.floor(daysIn / 7) + 1}`;
  return `Month ${Math.floor(daysIn / 30) + 1}`;
}

function JourneyCard({ journey, onSelect }: { journey: TimelineJourney; onSelect: (id: string) => void }) {
  const isRed = journey.attentionStatus === "needs_attention" || journey.attentionStatus === "tasks_overdue";
  const dotColor = STATUS_DOT_COLOR[journey.attentionStatus];

  const daysIn = journey.startDate ? differenceInCalendarDays(new Date(), parseISO(journey.startDate)) : null;
  const hasStarted = daysIn !== null && daysIn >= 0;

  const startLabel =
    journey.startDate && hasStarted
      ? formatTenureLabel(daysIn)
      : journey.startDate
        ? `Starts ${format(parseISO(journey.startDate), "MMM d")}`
        : null;

  const subtitleParts = [journey.title, startLabel].filter(Boolean);

  return (
    <Box
      as="button"
      type="button"
      onClick={() => onSelect(String(journey.id))}
      border="1px solid"
      borderColor={isRed ? "#FECACA" : "gray.100"}
      borderRadius="10px"
      mb="8px"
      bg="white"
      _hover={{ bg: "gray.50" }}
      transition="background 0.1s"
      w="100%"
      textAlign="left"
    >
      <Flex align="center" gap="10px" px="12px" py="9px">
        <Avatar name={journey.newHireName ?? ""} size="sm" flexShrink={0} />
        <Box flex="1" minW={0}>
          <Text fontSize="13px" fontWeight="600" color="content.primary" noOfLines={1}>
            {journey.newHireName ?? "—"}
          </Text>
          <Text fontSize="11px" color="content.tertiary" noOfLines={1}>
            {subtitleParts.join(" · ")}
          </Text>
        </Box>
        <Flex align="center" gap="6px" flexShrink={0}>
          <Box w="8px" h="8px" borderRadius="full" bg={dotColor} flexShrink={0} />
          <Icon as={TbChevronRight} color="content.tertiary" boxSize="14px" flexShrink={0} />
        </Flex>
      </Flex>
    </Box>
  );
}

function StatsRow({ stage }: { stage: TimelineStage }) {
  const needsAttention = stage.needsAttention + stage.tasksOverdue;
  return (
    <Flex
      align="center"
      gap="14px"
      pb="12px"
      mb="4px"
      borderBottom="0.5px solid"
      borderColor="gray.100"
      flexWrap="wrap"
    >
      <HStack spacing="5px">
        <Box w="8px" h="8px" borderRadius="full" bg="#DC2626" flexShrink={0} />
        <Text fontSize="12px" color="content.primary">
          {needsAttention} needs attention
        </Text>
      </HStack>
      <HStack spacing="5px">
        <Box w="8px" h="8px" borderRadius="full" bg="#3B82F6" flexShrink={0} />
        <Text fontSize="12px" color="content.primary">
          {stage.tasksOpen} open tasks, no problems
        </Text>
      </HStack>
      <HStack spacing="5px">
        <Box w="8px" h="8px" borderRadius="full" bg="#2D8A57" flexShrink={0} />
        <Text fontSize="12px" color="content.primary">
          {stage.allClear} all clear
        </Text>
      </HStack>
      <Text fontSize="12px" color="content.tertiary" ml="auto">
        {stage.count} {stage.count === 1 ? "hire" : "hires"}
      </Text>
    </Flex>
  );
}

function StageModal({
  stage, isOpen, onClose, onSelectJourney,
}: {
  stage: TimelineStage | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectJourney: (id: string) => void;
}) {
  if (!stage) return null;

  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Text fontSize="16px" fontWeight="700" color="content.primary">
            {stage.label}
          </Text>
          <Text fontSize="12px" fontWeight="400" color="content.tertiary" mt="2px">
            {stage.subtitle}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {stage.key === "pending" ? (
            <VStack spacing={0} align="stretch">
              {stage.journeys.map((journey) => (
                <PendingJourneyCard key={journey.id} journey={journey} />
              ))}
            </VStack>
          ) : (
            <>
              <StatsRow stage={stage} />
              <VStack spacing={0} align="stretch" mt={3}>
                {stage.journeys.map((journey) => (
                  <JourneyCard key={journey.id} journey={journey} onSelect={onSelectJourney} />
                ))}
              </VStack>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default function OnboardingTimelineCard() {
  const [selectedStageKey, setSelectedStageKey] = useState<string | null>(null);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);

  const { data } = useQuery<TimelineData>({
    queryKey: ["dashboardTimeline"],
    queryFn: () => apiClient.get(API_ROUTES.dashboard.timeline),
    staleTime: 1000 * 60 * 3,
  });

  const { data: selectedJourney } = useNewHireJourneyDetailQuery(selectedJourneyId ?? undefined, {
    enabled: !!selectedJourneyId,
  });

  if (!data || !data.stages || data.stages.every((s) => s.count === 0)) return null;

  const selectedStage = data.stages.find((s) => s.key === selectedStageKey) ?? null;

  return (
    <>
      <DashboardCard sx={{ "& .chakra-card__body": { py: "10px" } }}>
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="sm" fontWeight="600" color="content.primary">
            New Hires Onboarding Timeline
          </Text>
          <Text fontSize="11px" color="content.tertiary">
            {data.totalCount} active {data.totalCount === 1 ? "hire" : "hires"} · click a stage
          </Text>
        </Flex>
        <Flex align="flex-start">
          {data.stages.map((stage, i) => (
            <Fragment key={stage.key}>
              <StageNode stage={stage} onClick={() => setSelectedStageKey(stage.key)} />
              {i < data.stages.length - 1 && <Box flex={1} h="2px" bg="gray.200" mt="17px" flexShrink={1} mx="6px" />}
            </Fragment>
          ))}
        </Flex>
        <TimelineLegend />
      </DashboardCard>
      <StageModal
        stage={selectedStage}
        isOpen={selectedStageKey !== null}
        onClose={() => setSelectedStageKey(null)}
        onSelectJourney={setSelectedJourneyId}
      />
      <JourneyActionItemsDrawer
        journey={selectedJourney}
        isOpen={selectedJourneyId !== null}
        onClose={() => setSelectedJourneyId(null)}
      />
    </>
  );
}
